/**
 * =============================================================================
 * LICENSE SERVICE — Online & Offline License Verification
 * =============================================================================
 * Single source of truth for all license operations.
 * Components must NEVER call this service directly — use LicenseContext.
 */

import type {
  CachedLicenseState,
  LicenseVerifyRequest,
  LicenseVerifyResponse,
  OfflineActivationPayload,
  LicensePlan,
} from '../types/license';

// ─── Constants ────────────────────────────────────────────────────────────────

const CACHE_KEY = 'wf_license_state';
/** Cache TTL for a valid license: 24 h */
const VALID_TTL_MS = 24 * 60 * 60 * 1000;
/** Cache TTL after a failed check: 30 min (allow retry) */
const INVALID_TTL_MS = 30 * 60 * 1000;

/**
 * License verification endpoint.
 * In production this points to the Supabase Edge Function.
 * Override at build time via VITE_LICENSE_API_URL.
 */
const LICENSE_API_URL =
  (import.meta.env.VITE_LICENSE_API_URL as string | undefined) ||
  `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/license-verify`;

/**
 * HMAC key for offline activation tokens.
 * Set at build time via VITE_OFFLINE_ACTIVATION_SECRET.
 * Must match the value used when generating the offline token server-side.
 */
const OFFLINE_SECRET: string =
  (import.meta.env.VITE_OFFLINE_ACTIVATION_SECRET as string | undefined) ?? '';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function readCache(): CachedLicenseState | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: CachedLicenseState = JSON.parse(raw);
    // Remove expired cache
    if (Date.now() > parsed.cachedAt + parsed.ttl) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(state: Omit<CachedLicenseState, 'cachedAt' | 'ttl'>, valid: boolean): void {
  const entry: CachedLicenseState = {
    ...state,
    cachedAt: Date.now(),
    ttl: valid ? VALID_TTL_MS : INVALID_TTL_MS,
  };
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Storage quota exceeded — non-fatal
  }
}

function clearCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

// ─── HMAC-SHA256 via WebCrypto ────────────────────────────────────────────────

async function hmacSha256(message: string, secret: string): Promise<ArrayBuffer> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret).buffer as ArrayBuffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return crypto.subtle.sign('HMAC', keyMaterial, enc.encode(message).buffer as ArrayBuffer);
}

async function verifyHmacSha256(
  message: string,
  secret: string,
  signatureHex: string,
): Promise<boolean> {
  try {
    const sigBytes = hexToBytes(signatureHex);
    const enc = new TextEncoder();
    const keyData = enc.encode(secret).buffer as ArrayBuffer;
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['verify'],
    );
    // Copy into a fresh ArrayBuffer to satisfy SubtleCrypto's BufferSource constraint
    const sigBuf = new ArrayBuffer(sigBytes.byteLength);
    new Uint8Array(sigBuf).set(sigBytes);
    const msgData = enc.encode(message).buffer as ArrayBuffer;
    return crypto.subtle.verify('HMAC', keyMaterial, sigBuf, msgData);
  } catch {
    return false;
  }
}

function bufferToHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const buf = new ArrayBuffer(hex.length / 2);
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

// ─── Offline Grace Period ────────────────────────────────────────────────────

/**
 * Maximum time the app can operate offline after the last successful
 * server verification before requiring reconnection.
 */
const OFFLINE_GRACE_PERIOD_MS = 72 * 60 * 60 * 1000; // 72 hours
const GRACE_PERIOD_KEY = 'wf_license_grace_start';

/**
 * Returns a valid grace-period response when:
 *  - A server-verified cache entry exists for the given license key, AND
 *  - The elapsed offline time is within OFFLINE_GRACE_PERIOD_MS.
 *
 * SECURITY: Only grants access when `serverVerified === true` in cache.
 * UUID-only local activation is no longer accepted.
 */
function evaluateGracePeriod(licenseKey: string): LicenseVerifyResponse {
  const cached = readCache();

  if (
    !cached ||
    !cached.valid ||
    !cached.serverVerified ||
    cached.licenseKey?.toUpperCase() !== licenseKey.trim().toUpperCase()
  ) {
    return {
      valid: false,
      status: 'unknown',
      message: 'Não foi possível verificar a licença. Verifique sua conexão com a internet e tente novamente.',
    };
  }

  let graceStart: number;
  try {
    const stored = localStorage.getItem(GRACE_PERIOD_KEY);
    if (stored) {
      graceStart = Number(stored);
    } else {
      graceStart = Date.now();
      localStorage.setItem(GRACE_PERIOD_KEY, String(graceStart));
    }
  } catch {
    graceStart = Date.now();
  }

  const elapsed = Date.now() - graceStart;

  if (elapsed > OFFLINE_GRACE_PERIOD_MS) {
    clearCache();
    try { localStorage.removeItem(GRACE_PERIOD_KEY); } catch { /* ignore */ }
    return {
      valid: false,
      status: 'expired',
      message: 'Licença não verificada nos últimos 72 horas. Conecte-se à internet e inicie o aplicativo novamente.',
    };
  }

  const hoursRemaining = Math.ceil((OFFLINE_GRACE_PERIOD_MS - elapsed) / (60 * 60 * 1000));
  return {
    valid: true,
    plan: cached.plan,
    maxUsers: cached.maxUsers,
    expiresAt: cached.expiresAt,
    status: 'valid',
    message: `Modo offline ativo — verificação necessária em até ${hoursRemaining}h.`,
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const LicenseService = {
  /**
   * Returns the current cached license state without a network call.
   * Returns null if the cache is absent or expired.
   */
  getCachedState(): CachedLicenseState | null {
    return readCache();
  },

  /**
   * Performs an online license verification call.
   * Caches the result on success.
   */
  async verifyOnline(
    licenseKey: string,
    machineId: string,
    version: string,
  ): Promise<LicenseVerifyResponse> {
    if (!licenseKey.trim()) {
      return { valid: false, status: 'invalid', message: 'License key is required' };
    }

    const body: LicenseVerifyRequest = {
      licenseKey: licenseKey.trim().toUpperCase(),
      machineId,
      version,
    };

    let response: Response;
    try {
      response = await fetch(LICENSE_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY ?? '',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''}`,
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(10_000),
      });
    } catch {
      // Network unreachable — apply grace period logic
      return evaluateGracePeriod(licenseKey);
    }

    if (!response.ok) {
      // Server error — apply grace period logic
      return evaluateGracePeriod(licenseKey);
    }

    const result: LicenseVerifyResponse = await response.json();

    // Server responded — clear any grace period timer
    try { localStorage.removeItem(GRACE_PERIOD_KEY); } catch { /* ignore */ }

    // Cache the result, marking it as server-verified so grace period is valid
    writeCache(
      {
        valid: result.valid,
        plan: result.plan,
        maxUsers: result.maxUsers,
        expiresAt: result.expiresAt,
        licenseKey: licenseKey.trim().toUpperCase(),
        serverVerified: result.valid, // only mark verified when server confirms valid
      },
      result.valid,
    );

    return result;
  },

  /**
   * Validates an offline activation token and stores the resulting state.
   *
   * Token format: "<base64url(payload_json)>.<hex_hmac_signature>"
   *
   * The server generates these tokens using:
   *   HMAC-SHA256(JSON.stringify(payload), OFFLINE_SECRET)
   */
  async activateOffline(
    rawToken: string,
    currentMachineId: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const parts = rawToken.trim().split('.');
      if (parts.length !== 2) {
        return { success: false, message: 'Invalid token format' };
      }

      const [payloadB64, signatureHex] = parts;

      // Decode payload
      let payload: OfflineActivationPayload;
      try {
        const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
        payload = JSON.parse(json);
      } catch {
        return { success: false, message: 'Invalid token — cannot decode payload' };
      }

      // Validate machine ID matches this machine
      if (payload.machineId !== currentMachineId) {
        return {
          success: false,
          message: 'This offline token was issued for a different machine',
        };
      }

      // Validate expiry
      if (payload.expiresAt) {
        const expiry = new Date(payload.expiresAt).getTime();
        if (Date.now() > expiry) {
          return { success: false, message: 'Offline activation token has expired' };
        }
      }

      // Verify HMAC signature
      if (!OFFLINE_SECRET) {
        console.warn('[LicenseService] VITE_OFFLINE_ACTIVATION_SECRET not set — skipping signature check');
      } else {
        const canonical = payloadB64; // sign the encoded payload string
        const valid = await verifyHmacSha256(canonical, OFFLINE_SECRET, signatureHex);
        if (!valid) {
          return { success: false, message: 'Offline token signature is invalid' };
        }
      }

      // Store as valid cached state (offline TTL = until expiry or 90 days)
      const expiryMs = payload.expiresAt
        ? new Date(payload.expiresAt).getTime() - Date.now()
        : 90 * 24 * 60 * 60 * 1000;

      const entry: CachedLicenseState = {
        valid: true,
        plan: payload.plan as LicensePlan,
        maxUsers: payload.maxUsers,
        expiresAt: payload.expiresAt,
        licenseKey: payload.licenseKey,
        cachedAt: Date.now(),
        ttl: Math.min(expiryMs, 90 * 24 * 60 * 60 * 1000),
        offlineActivated: true,
      };

      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
      } catch {
        // Non-fatal
      }

      return { success: true, message: 'Offline activation successful' };
    } catch (err: any) {
      console.error('[LicenseService] Offline activation error:', err);
      return { success: false, message: 'Activation failed — please try again' };
    }
  },

  /** Generate an offline token (server-side / admin tool use only) */
  async generateOfflineToken(
    payload: OfflineActivationPayload,
    secret: string,
  ): Promise<string> {
    const payloadB64 = btoa(JSON.stringify(payload))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    const sigBuf = await hmacSha256(payloadB64, secret);
    const sigHex = bufferToHex(sigBuf);
    return `${payloadB64}.${sigHex}`;
  },

  clearCache,
};
